import type { RuleBoxConfig } from "@loot-os/api-contract";
import {
  emptyRuleBoxForm,
  parseRuleBoxForm,
  type RuleBoxForm,
  ruleBoxFormFromConfig,
} from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { BoxNameStep } from "./BoxNameStep";
import { Blockers, StepHeader } from "./BuilderParts";
import { BUILDER_STEPS } from "./builderSteps";
import { PriceStep } from "./PriceStep";
import { RulesStep } from "./RulesStep";
import { slugify } from "./slug";
import { useTRPC } from "./trpc";
import { useBoxImage } from "./useBoxImage";
import { useRuleBoxPreview } from "./useRuleBoxPreview";

/** The lines that are set, for a list of what still blocks a page. */
function blocking(...lines: (string | false | undefined)[]): string[] {
  return lines.filter((line): line is string => Boolean(line));
}

/** The box an edit opens on: what it is saved with. */
interface RuleBoxBuilderStart {
  boxId: string;
  name: string;
  imageUrl: string;
  /** 0 while the box's rules have never built a drop table, so it has nothing to sell. */
  currentVersion: number;
  config: RuleBoxConfig;
}

/**
 * Creates or edits a rule box, one page per step: a name and an optional image, the rules that
 * pick the shop's products (with the live estimate beside them), and the margin and price. The
 * server solves the drop table on save and again every day.
 *
 * A new box is refused here while its rules cannot build a box, since it could never go on sale.
 * An edit always saves: renaming a box whose rules stopped solving has to work, and the box keeps
 * selling its last drop table until they solve again.
 */
export function RuleBoxBuilder({
  start,
  onDone,
  onCancel,
}: {
  /** The box being edited, or null for a new one. */
  start: RuleBoxBuilderStart | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const facets = useQuery(trpc.catalog.facets.queryOptions());
  const editing = start !== null;
  const [step, setStep] = useState(0);
  // Set when "Next" is pressed on a page that is not finished: its blockers then show.
  const [tried, setTried] = useState(false);
  const [name, setName] = useState(start?.name ?? "");
  const image = useBoxImage(start?.imageUrl ?? "");
  const [form, setForm] = useState<RuleBoxForm>(() =>
    start ? ruleBoxFormFromConfig(start.config) : emptyRuleBoxForm(),
  );
  // The saved config as the form reads it back, to tell whether an edit changed it.
  const [startKey] = useState(() => {
    if (!start) return null;
    const parsed = parseRuleBoxForm(ruleBoxFormFromConfig(start.config));
    return parsed.ok ? JSON.stringify(parsed.config) : null;
  });
  // The last slug the server said is taken, so the name page refuses it until the name changes.
  const [takenSlug, setTakenSlug] = useState<string | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const create = useMutation(trpc.boxes.create.mutationOptions());
  const update = useMutation(trpc.boxes.update.mutationOptions());

  const parsed = parseRuleBoxForm(form);
  const errors = parsed.ok ? {} : parsed.errors;
  const preview = useRuleBoxPreview(parsed, start?.boxId);
  const refusal = preview.current && !preview.current.ok ? preview.current : null;
  const slug = slugify(name);
  const busy = create.isPending || update.isPending;
  // A new box waits for the estimate of the form as it stands before it may go on.
  const waiting =
    !editing && step > 0 && parsed.ok && preview.current === undefined && !preview.error;

  const blockers = [
    blocking(
      !name.trim() && "Give the box a name.",
      !editing && name.trim() !== "" && !slug && "Use at least one letter or number in the name.",
      !editing &&
        slug !== "" &&
        slug === takenSlug &&
        "Another box already uses this name. Pick a different one.",
    ),
    blocking(
      errors.minPrice,
      errors.maxPrice,
      errors.minItems,
      errors.maxItems,
      errors.fixedPrice,
      errors.marginPercent,
      !editing && preview.error !== null && "The estimate could not be loaded. Try again.",
      !editing &&
        refusal !== null &&
        refusal.refusal !== "fixed_price_unsupported" &&
        `Change your rules so we can build the box: ${refusal.message}`,
    ),
    blocking(
      errors.marginPercent,
      errors.minReturnPercent,
      errors.fixedPrice,
      !editing && preview.error !== null && "The estimate could not be loaded. Try again.",
      !editing && refusal !== null && `Change the price or rules: ${refusal.message}`,
    ),
  ];
  const stepBlockers = blockers[step] ?? [];
  const last = step === BUILDER_STEPS.length - 1;

  function patchForm(patch: Partial<RuleBoxForm>) {
    setForm((f) => ({ ...f, ...patch }));
  }

  function goTo(next: number) {
    setTried(false);
    setStep(next);
  }

  function onNext() {
    if (stepBlockers.length > 0) {
      setTried(true);
      return;
    }
    if (last) void submit();
    else if (step === 0 && !editing) void leaveNameStep();
    else goTo(step + 1);
  }

  /** Asks whether the name's slug is free, so a taken one is fixed here and not at "Create box". */
  async function leaveNameStep() {
    setCheckingSlug(true);
    try {
      const { available } = await queryClient.fetchQuery({
        ...trpc.boxes.slugAvailable.queryOptions({ slug }),
        staleTime: 0,
      });
      if (available) {
        goTo(1);
      } else {
        setTakenSlug(slug);
        setTried(true);
      }
    } catch (error) {
      shopify.toast.show(error instanceof Error ? error.message : "Could not check the name.", {
        isError: true,
      });
    } finally {
      setCheckingSlug(false);
    }
  }

  async function submit() {
    if (!parsed.ok) return;
    const trimmed = name.trim();
    try {
      if (start) {
        const configChanged = JSON.stringify(parsed.config) !== startKey;
        const { preview: solved, listingSynced } = await update.mutateAsync({
          boxId: start.boxId,
          ...(trimmed !== start.name ? { name: trimmed } : {}),
          ...(image.url !== start.imageUrl ? { imageUrl: image.url } : {}),
          ...(configChanged ? { config: parsed.config } : {}),
        });
        if (!solved.ok) {
          shopify.toast.show(`${trimmed} saved, but ${solved.message}`, { isError: true });
        } else if (!listingSynced) {
          shopify.toast.show(
            "Saved, but your store's product didn't update. Save again to retry.",
            {
              isError: true,
            },
          );
        } else {
          shopify.toast.show(`${trimmed} saved.`);
        }
      } else {
        const { preview: solved } = await create.mutateAsync({
          name: trimmed,
          slug,
          imageUrl: image.url || undefined,
          config: parsed.config,
        });
        if (solved.ok) {
          shopify.toast.show(`${trimmed} is ready. Switch on "Sell in store" to list it.`);
        } else {
          shopify.toast.show(`${trimmed} saved, but it can't go on sale yet: ${solved.message}`, {
            isError: true,
          });
        }
      }
    } catch {
      // Already a toast (queryClient's MutationCache); the builder stays open to try again.
      return;
    }
    onDone();
  }

  return (
    <s-stack gap="large">
      <s-heading>{start ? `Edit ${start.name}` : "Create a box"}</s-heading>
      <StepHeader step={step} />

      {step === 0 && <BoxNameStep name={name} onName={setName} image={image} />}
      {step === 1 && (
        <RulesStep
          form={form}
          onChange={patchForm}
          errors={errors}
          facets={facets.data}
          facetsLoading={facets.isLoading}
          preview={preview}
        />
      )}
      {step === 2 && (
        <PriceStep form={form} onChange={patchForm} errors={errors} preview={preview} />
      )}

      {start && step > 0 && refusal && (
        <s-banner tone="warning">
          {start.currentVersion > 0
            ? "You can still save: the box shows sold out in your store until its rules can build it again."
            : "You can still save, but the box can't go on sale until its rules can build it."}
        </s-banner>
      )}
      {tried && <Blockers lines={stepBlockers} />}

      <s-stack direction="inline" gap="base" justifyContent="space-between">
        <s-button disabled={busy || undefined} onClick={onCancel}>
          Cancel
        </s-button>
        <s-stack direction="inline" gap="base">
          {step > 0 && (
            <s-button disabled={busy || undefined} onClick={() => goTo(step - 1)}>
              Back
            </s-button>
          )}
          <s-button
            variant="primary"
            disabled={busy || checkingSlug || image.uploading || waiting || undefined}
            loading={busy || checkingSlug || waiting || undefined}
            onClick={onNext}
          >
            {last ? (editing ? "Save changes" : "Create box") : "Next"}
          </s-button>
        </s-stack>
      </s-stack>
    </s-stack>
  );
}
